import { useMemo } from "react";
import { MessageDescriptor } from "react-intl";

import useI18n from "@I18n/hooks/useI18n";
import { CategoryKey } from "@UI/Components/CategoryPill/CategoryPill";

import { STEP_ICONS } from "../config/constants";
import { getCategoryMessage } from "../utils/messageKeys";
import { useTaskCreationFlow } from "../Contexts/TaskCreationFlow";
import {
  ITaskCreationSummaryCategory,
  ITaskCreationSummaryStep,
  TTaskCreationSummaryStepStatus,
} from "../Components/TaskCreationSummary/TaskCreationSummary";
import { IAnswerMap, IFlowScreen, IFlowStep } from "../types";
import { isScreenComplete } from "../utils/buildFlowShape";
import messages from "../messages";
import { useCategoryKey } from "./useCategoryTree";

/**
 * A step is in progress while the user is on one of its screens, complete once
 * every one of them is, and pending otherwise.
 *
 * The current step wins over completeness on purpose: the last screen of a step
 * can be fully answered while the user is still standing on it (they arrived
 * with prefilled answers, or came back to edit), and calling that "completed"
 * while it is on screen reads as though the panel had skipped ahead of them.
 *
 * A step with no screens of its own — `contact_details`, which the frontend
 * appends for a visitor — is pending rather than vacuously complete.
 */
const deriveStepStatus = (
  step: IFlowStep,
  {
    currentStepKey,
    screens,
    answers,
    isSubcategoryChosen,
  }: {
    currentStepKey?: string;
    screens: IFlowScreen[];
    answers: IAnswerMap;
    isSubcategoryChosen: boolean;
  }
): TTaskCreationSummaryStepStatus => {
  if (step.key === currentStepKey) return "inProgress";

  const stepScreens = screens.filter((screen) => screen.stepKey === step.key);

  if (
    stepScreens.length > 0 &&
    stepScreens.every((screen) =>
      isScreenComplete(screen, answers, { isSubcategoryChosen })
    )
  )
    return "completed";

  return "pending";
};

/**
 * The step's own name. Looked up rather than switched on: the config decides
 * the step keys, so an unnamed one falls back to nothing and the row reads
 * "Step 2" instead of throwing.
 */
const getStepNameMessage = (stepKey: string): MessageDescriptor | undefined =>
  (messages as Record<string, MessageDescriptor>)[
    `taskCreation.step.${stepKey}`
  ];

/**
 * The current step's name, for the desktop progress row. `undefined` outside
 * the steps (the description screen) and while the shape is still a floor with
 * no name of its own.
 */
export const useCurrentStepName = (): string | undefined => {
  const { currentStep } = useTaskCreationFlow();
  const { formatMessage } = useI18n();
  const message = currentStep ? getStepNameMessage(currentStep.key) : undefined;

  return message ? formatMessage(message) : undefined;
};

export interface IUseTaskCreationSummaryResult {
  /** `undefined` until the category tree resolves — the caller renders the
   * rows without a pill rather than a half-empty one. */
  category?: ITaskCreationSummaryCategory;
  steps: ITaskCreationSummaryStep[];
}

/**
 * The summary panel's rows, derived from the flow rather than passed down.
 *
 * The panel and the mobile modal both call this and so cannot disagree about
 * which step is in progress, and neither container needs to know how a step's
 * status is computed. Everything comes from `useTaskCreationFlow()` plus the
 * category tree, which is already cached for the description screen.
 */
export const useTaskCreationSummary = (): IUseTaskCreationSummaryResult => {
  const { steps, screens, currentStep, answers, draft, pCategoryId } =
    useTaskCreationFlow();
  const { formatMessage } = useI18n();

  // Two lookups against one cached tree: the theme decides the pill's icon
  // (`CategoryKey` is the theme namespace — "diy", not "painter"), the
  // subcategory names it.
  const { keyName: themeKey } = useCategoryKey({ pCategoryId });
  const { keyName: subCategoryKey } = useCategoryKey({
    sCategoryId: draft?.s_category_id,
  });

  const isSubcategoryChosen = Boolean(draft?.s_category_id);

  return useMemo(() => {
    const themeMessage = themeKey ? getCategoryMessage(themeKey) : undefined;
    const subCategoryMessage = subCategoryKey
      ? getCategoryMessage(subCategoryKey)
      : undefined;

    return {
      // Without a theme there is no icon and no name, which is the whole pill.
      category:
        themeKey && themeMessage
          ? {
              categoryKey: themeKey as CategoryKey,
              name: formatMessage(themeMessage),
              subCategoryName: subCategoryMessage
                ? formatMessage(subCategoryMessage)
                : undefined,
            }
          : undefined,
      steps: steps.map((step, index) => {
        const nameMessage = getStepNameMessage(step.key);

        return {
          key: step.key,
          icon: STEP_ICONS[step.key],
          label: formatMessage(messages["taskCreation.summary.stepLabel"], {
            number: index + 1,
            name: nameMessage ? formatMessage(nameMessage) : "",
          }),
          status: deriveStepStatus(step, {
            currentStepKey: currentStep?.key,
            screens,
            answers,
            isSubcategoryChosen,
          }),
        };
      }),
    };
  }, [
    steps,
    screens,
    currentStep,
    answers,
    isSubcategoryChosen,
    themeKey,
    subCategoryKey,
    formatMessage,
  ]);
};
